"use strict";
/**
 * Monthly PnL — one card per month, net across venues plus each venue's share.
 *
 * Pure: `buildMonthlyModel` shapes the /pnl/totals response, `renderPnlMonthly`
 * draws it; the handler owns fetching. Months are the server's realization-date
 * buckets in exchange time (ai/PNL_TOTALS_PLAN.md §19).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildMonthlyModel = buildMonthlyModel;
exports.renderPnlMonthly = renderPnlMonthly;
const pnl_time_1 = require("../../shared/pnl-time");
const venues_1 = require("../../shared/venues");
const pnl_display_1 = require("./pnl-display");
/** Venue rows always appear in this order, so months read alike. */
const VENUE_ORDER = ["kalshi", "pm-us", "novig"];
const monthOf = (at) => (0, pnl_time_1.zonedDay)(at).slice(0, 7);
function previousMonth(period) {
    const [year, month] = period.split("-").map(Number);
    return month === 1
        ? `${year - 1}-12`
        : `${year}-${String(month - 1).padStart(2, "0")}`;
}
function emptyMonth(period) {
    return {
        period,
        net: 0,
        realized: 0,
        fees: 0,
        settledMarkets: 0,
        venues: [],
        current: false,
        partial: false,
    };
}
/**
 * Month cards from a `bucket=month&groupBy=venue` response. A month's figures are
 * the sum of its venue rows (PnL is additive), and every month from the first
 * settled one to now gets a card — a quiet month shows as one, not as a gap.
 */
function buildMonthlyModel(result, now) {
    const byPeriod = new Map();
    for (const b of result.buckets) {
        if (!b.period)
            continue; // open capital has no month yet
        const month = byPeriod.get(b.period) ?? emptyMonth(b.period);
        month.net += b.net;
        month.realized += b.realized;
        month.fees += b.fees;
        month.settledMarkets += b.settledMarkets;
        month.venues.push({ venue: b.venue ?? "unknown", net: b.net });
        byPeriod.set(b.period, month);
    }
    const current = monthOf(now);
    const loggingBegan = result.dataFrom ? monthOf(new Date(result.dataFrom)) : null;
    const first = [...byPeriod.keys()].sort()[0];
    const months = [];
    if (first) {
        // `YYYY-MM` sorts lexically, so string comparison walks the calendar.
        for (let p = current > first ? current : first; p >= first; p = previousMonth(p)) {
            const month = byPeriod.get(p) ?? emptyMonth(p);
            month.venues.sort((a, b) => rank(a.venue) - rank(b.venue));
            month.current = p === current;
            month.partial = p === loggingBegan;
            months.push(month);
        }
    }
    return {
        months,
        openCost: result.totals.openCost,
        openPositions: result.totals.openPositions,
        uncheckedMarkets: result.uncheckedMarkets,
    };
}
/** Unknown venues sort last rather than throwing the order off. */
function rank(venue) {
    const i = VENUE_ORDER.indexOf(venue);
    return i === -1 ? VENUE_ORDER.length : i;
}
function monthLabel(period) {
    const [year, month] = period.split("-").map(Number);
    // Built at UTC midnight and formatted in UTC, so no zone can shift the month.
    return new Date(Date.UTC(year, month - 1, 1)).toLocaleString("en-US", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    });
}
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
function renderCard(month) {
    const tags = [
        month.current ? `<span class="pnl-month-tag">so far</span>` : "",
        month.partial ? `<span class="pnl-month-tag">partial</span>` : "",
    ].join("");
    const body = month.venues.length === 0
        ? `<div class="pnl-month-empty">No settled bets</div>`
        : `<div class="pnl-month-venues">${month.venues
            .map((v) => `<div class="pnl-month-venue">
							<span>${(0, pnl_display_1.esc)((0, venues_1.isVenueId)(v.venue) ? (0, venues_1.venueLabel)(v.venue) : v.venue)}</span>
							${(0, pnl_display_1.pnlCell)(v.net)}
						</div>`)
            .join("")}</div>
				<div class="pnl-month-meta">Realized ${(0, pnl_display_1.pnlCell)(month.realized)} · Fees ${(0, pnl_display_1.usd)(month.fees)} · ${plural(month.settledMarkets, "market")}</div>`;
    return `<div class="pnl-month-card${month.venues.length === 0 ? " is-empty" : ""}" data-period="${month.period}">
		<div class="pnl-month-head">
			<span class="pnl-month-label">${monthLabel(month.period)}${tags}</span>
			<span class="pnl-month-net">${month.venues.length === 0 ? "" : (0, pnl_display_1.pnlCell)(month.net)}</span>
		</div>
		${body}
	</div>`;
}
/** Draw the monthly view: open capital, a catch-up warning if owed, then months. */
function renderPnlMonthly(model, el) {
    const open = model.openPositions > 0
        ? `<div class="pnl-monthly-open">Open now: <strong>${(0, pnl_display_1.usd)(model.openCost)}</strong> in ${plural(model.openPositions, "market")}</div>`
        : "";
    const catchUp = model.uncheckedMarkets > 0
        ? `<div class="pnl-monthly-catchup">${plural(model.uncheckedMarkets, "held market")} not checked for a result yet — recent months may be missing them.</div>`
        : "";
    const months = model.months.length === 0
        ? `<div class="pnl-empty">No settled bets yet.</div>`
        : model.months.map(renderCard).join("");
    el.innerHTML = open + catchUp + months;
}
